import { PageHero } from "@/components/PageHero";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    BookOpen, Brain, AlertTriangle, Stethoscope, Layers, ListOrdered,
    Phone, ExternalLink, ClipboardCheck, RefreshCcw, ArrowRight, HandHeart,
    Home, Users2, Coffee, Building2, BedDouble, HeartPulse, Sparkles, Network,
    Compass, Wand2,
} from "lucide-react";
import KnowledgeLibrarySection from "@/components/KnowledgeLibrarySection";

/* -------------------------------------------------------------------------- */
/*  Static educational content — plain-language, sourced from public-domain   */
/*  references (Alzheimer's Association, NIH National Institute on Aging,     */
/*  Reisberg FAST scale). No medical advice — see footer disclaimer.          */
/* -------------------------------------------------------------------------- */

const SECTIONS = [
    { id: "what-is", label: "What is dementia?", Icon: Brain },
    { id: "risk-factors", label: "Risk factors", Icon: AlertTriangle },
    { id: "diagnosis", label: "How it's diagnosed", Icon: Stethoscope },
    { id: "types", label: "Types of dementia", Icon: Layers },
    { id: "fast-scale", label: "FAST stages", Icon: ListOrdered },
    { id: "knowledge-library", label: "Knowledge library", Icon: BookOpen },
];



const TYPES = [
    {
        name: "Alzheimer's disease",
        prevalence: "60–80% of cases",
        hallmark: "Memory loss is usually the first sign — especially recent memories. Caused by amyloid plaques and tau tangles in the brain.",
        progression: "Slow and steady, typically over 8–12 years.",
        tone: "teal",
    },
    {
        name: "Vascular dementia",
        prevalence: "5–10% of cases",
        hallmark: "Caused by reduced blood flow to the brain — often after strokes (large or many tiny silent ones). Difficulty with planning and decision-making often appears before memory issues.",
        progression: "Stepwise — sudden drops, then plateaus.",
        tone: "blue",
    },
    {
        name: "Lewy body dementia (LBD)",
        prevalence: "5–10% of cases",
        hallmark: "Visual hallucinations, fluctuating alertness day-to-day, movement problems (like Parkinson's), and acting out dreams.",
        progression: "Variable. Symptoms come and go more than in Alzheimer's.",
        tone: "yellow",
    },
    {
        name: "Frontotemporal dementia (FTD)",
        prevalence: "Often diagnosed under age 60",
        hallmark: "Personality and behavior changes come first — not memory. Loss of empathy, impulsive choices, or trouble finding words.",
        progression: "Can be faster than Alzheimer's. Younger onset is common.",
        tone: "coral",
    },
    {
        name: "Mixed dementia",
        prevalence: "Common in people over 80",
        hallmark: "More than one type at once — most often Alzheimer's + vascular changes. Symptoms overlap.",
        progression: "Depends on the combination.",
        tone: "sage",
    },
    {
        name: "Parkinson's disease dementia",
        prevalence: "~30–50% of Parkinson's patients eventually",
        hallmark: "Develops at least a year after Parkinson's motor symptoms. Slowed thinking, attention problems, sometimes hallucinations.",
        progression: "Gradual, follows the Parkinson's trajectory.",
        tone: "teal",
    },
    {
        name: "Normal pressure hydrocephalus (NPH)",
        prevalence: "Rare but important",
        hallmark: "Triad: trouble walking + memory issues + urinary urgency. *Potentially reversible* with a shunt — always worth ruling out.",
        progression: "Can plateau or improve with treatment.",
        tone: "blue",
    },
    {
        name: "Creutzfeldt-Jakob & Huntington's",
        prevalence: "Very rare",
        hallmark: "Rapid progression (CJD) or strong family history (Huntington's). Specialist diagnosis required.",
        progression: "CJD: months. Huntington's: 10–25 years.",
        tone: "coral",
    },
];

const FAST_STAGES = [
    { n: 1, name: "Normal aging", caregiver: "No memory complaints. Functions as always.", tone: "teal" },
    { n: 2, name: "Very mild changes", caregiver: "Forgets familiar names or where they put things. Often called 'normal aging' — only the person notices.", tone: "teal" },
    { n: 3, name: "Mild cognitive impairment", caregiver: "Co-workers and family start to notice. Trouble at work, getting lost in new places, repeating questions. This is when many families first wonder.", tone: "sage" },
    { n: 4, name: "Mild dementia", caregiver: "Difficulty with complex tasks: managing finances, planning a dinner, traveling alone. Often still independent for personal care.", tone: "yellow" },
    { n: 5, name: "Moderate dementia", caregiver: "Needs help choosing clothes appropriate for the weather or occasion. May forget major details (address, phone). Can usually still feed and toilet themselves.", tone: "yellow" },
    { n: 6, name: "Moderately severe dementia", caregiver: "Help needed with dressing (6a), bathing (6b), and toileting (6c–6e). May not recognize family. Sleep cycles disrupted. This is often when full-time care begins.", tone: "coral" },
    { n: 7, name: "Severe dementia", caregiver: "Speech reduced to a few words (7a–7b), then lost. Loss of ability to walk (7c), sit up (7d), smile (7e), and hold up head (7f). Comfort and dignity become the priorities.", tone: "coral" },
];

// Yes/no markers — one per ascending stage. The HIGHEST stage marked "yes" = estimate.
const FAST_QUIZ = [
    { stage: 2, q: "Does your loved one occasionally forget familiar names or where they put things — in a way only they notice?" },
    { stage: 3, q: "Have co-workers, friends, or family started noticing changes — trouble at work, getting lost in new places, or repeating the same questions?" },
    { stage: 4, q: "Does your loved one have clear difficulty with complex tasks — managing finances, planning a meal, or traveling somewhere new on their own?" },
    { stage: 5, q: "Does your loved one need help choosing clothes appropriate for the weather or occasion, or forget major details like their address or phone number?" },
    { stage: 6, q: "Does your loved one need hands-on help with dressing, bathing, or using the bathroom?" },
    { stage: 7, q: "Is your loved one's speech limited to just a few words or hard to understand?" },
    { stage: 7, q: "Has your loved one lost the ability to walk without help, sit up unaided, or hold up their head?" },
];

const QUIZ_RESULT_COPY = {
    1: { title: "Likely stage 1 — Normal", body: "Based on what you shared, your loved one is functioning typically for their age. Keep an eye on changes over time and revisit this if anything new comes up.", tone: "teal" },
    2: { title: "Likely stage 2 — Very mild changes", body: "Small slips that don't yet interfere with daily life. This is a good time to start tracking patterns and have a calm baseline conversation with their doctor.", tone: "teal" },
    3: { title: "Likely stage 3 — Mild cognitive impairment", body: "Others are starting to notice. This is the stage where many families seek formal evaluation. A memory clinic or geriatrician can confirm what's happening and rule out reversible causes.", tone: "sage" },
    4: { title: "Likely stage 4 — Mild dementia", body: "Complex tasks have become hard. Now is the time to set up powers of attorney, simplify finances, and start building a support team if you haven't already.", tone: "yellow" },
    5: { title: "Likely stage 5 — Moderate dementia", body: "Daily decisions need scaffolding from you. Routines, visual cues, and removing choices (laying out one outfit) reduce stress for both of you.", tone: "yellow" },
    6: { title: "Likely stage 6 — Moderately severe dementia", body: "Hands-on caregiving has become the rhythm of your days. This is often when respite care, adult day programs, or in-home aides become essential — not optional.", tone: "coral" },
    7: { title: "Likely stage 7 — Severe dementia", body: "Your loved one needs full physical care. At this stage (especially FAST 7c+), Medicare hospice eligibility opens up — a benefit many families don't realize they qualify for. Comfort, gentleness, and presence matter most now.", tone: "coral" },
};

const TONE_STYLES = {
    teal: "bg-teal-soft text-teal-dark border-teal-soft",
    sage: "bg-[#DDE5D3] text-[#4F6B3D] border-[#DDE5D3]",
    blue: "bg-ml_blue-light text-ml_blue-dark border-ml_blue-light",
    yellow: "bg-yellow-soft text-yellow-dark border-yellow-soft",
    coral: "bg-coral-light text-coral-dark border-coral-light",
};



export default function Education() {
    const [active, setActive] = useState("what-is");

    const scrollTo = (id) => {
        setActive(id);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="space-y-10 animate-fade-in-up">
            {/* Header */}
            <PageHero
                kicker="Learn"
                kickerIcon={BookOpen}
                title="Learn the basics"
                subtitle="No one teaches you this stuff before you need it. Here’s plain-language answers to the questions most caregivers ask in the first few weeks."
            />

            {/* Learn categories — every education destination lives here now */}
            <section data-testid="learn-categories">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                        { key: "basics", title: "Dementia Basics", desc: "What dementia is, early signs, and what changes may mean.", onClick: () => scrollTo("what-is"), Icon: Brain },
                        { key: "diagnosis", title: "Diagnosis", desc: "How dementia is diagnosed and what to bring to the workup.", onClick: () => scrollTo("diagnosis"), Icon: Stethoscope },
                        { key: "fast-stages", title: "FAST Stages", desc: "The 7-stage functional scale clinicians use — where things are now.", onClick: () => scrollTo("fast-scale"), Icon: ListOrdered },
                        { key: "types", title: "Types of Dementia", desc: "Alzheimer's, Lewy body, vascular, frontotemporal, and MCI.", onClick: () => scrollTo("types"), Icon: Network },
                        { key: "behaviors", title: "Behaviors", desc: "Sundowning, wandering, agitation — practical, compassionate steps.", to: "/guidance", Icon: HandHeart },
                        { key: "treatments", title: "Treatments & Medications", desc: "Today's medications, disease-modifying options, and what to ask.", to: "/treatments", Icon: HeartPulse },
                        { key: "planning", title: "Care Planning", desc: "Hard conversations, living decisions, and planning ahead.", to: "/conversations", Icon: Compass },
                        { key: "legal-financial", title: "Legal & Financial", desc: "Medicare, Medicaid, VA benefits, POA, and what things really cost.", to: "/who-pays", Icon: Building2 },
                        { key: "research-trials", title: "Research & Clinical Trials", desc: "Currently recruiting studies near you or remote.", to: "/treatments#trials", Icon: ClipboardCheck },
                    ].map(c => {
                        const inner = (
                            <>
                                <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-teal-soft text-teal-dark shrink-0">
                                    <c.Icon size={17} strokeWidth={1.7} />
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-sm font-semibold text-[#1F2624]">{c.title}</span>
                                    <span className="block text-xs text-[#4C5551] mt-0.5 leading-snug">{c.desc}</span>
                                </span>
                            </>
                        );
                        const cls = "flex items-start gap-3 ml-card p-4 bg-white hover:border-teal transition-colors text-left w-full";
                        return c.to ? (
                            <Link key={c.key} to={c.to} className={cls} data-testid={`learn-cat-${c.key}`}>{inner}</Link>
                        ) : (
                            <button key={c.key} type="button" onClick={c.onClick} className={cls} data-testid={`learn-cat-${c.key}`}>{inner}</button>
                        );
                    })}
                </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8 lg:gap-12">
                {/* Sticky side nav */}
                <aside className="lg:sticky lg:top-32 lg:self-start no-print" data-testid="education-nav">
                    <nav className="ml-card p-3">
                        <ul className="space-y-1">
                            {SECTIONS.map(({ id, label, Icon }) => (
                                <li key={id}>
                                    <button
                                        onClick={() => scrollTo(id)}
                                        data-testid={`education-nav-${id}`}
                                        className={`w-full text-left inline-flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${active === id
                                            ? "bg-teal text-white"
                                            : "text-[#4C5551] hover:bg-cream-dark"
                                            }`}
                                    >
                                        <Icon size={16} strokeWidth={1.6} /> {label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </aside>

                {/* Main content */}
                <article className="space-y-16">
                    {/* What is dementia */}
                    <section id="what-is" className="scroll-mt-32" data-testid="education-what-is">
                        <h2 className="text-3xl font-heading font-semibold tracking-tight text-[#12776F]">What is dementia?</h2>
                        <p className="mt-4 text-[#1F2624] leading-relaxed">
                            <strong>Dementia isn’t one disease — it’s a word for a group of symptoms.</strong> The symptoms include
                            memory loss, trouble with language, difficulty making decisions, and changes in personality or
                            behavior. They have to be severe enough to interfere with daily life.
                        </p>
                        <p className="mt-4 text-[#1F2624] leading-relaxed">
                            Think of it like the word ”fever.” A fever can be caused by the flu, an ear infection, or
                            many other things. Dementia can be caused by Alzheimer’s, strokes, Lewy bodies, and others.
                            <strong> Figuring out which type matters</strong> because it changes what to expect and what helps.
                        </p>

                        <div className="ml-card p-5 mt-6 bg-teal-soft !border-teal-soft" data-testid="education-key-facts">
                            <h3 className="font-heading font-medium text-teal-dark">A few grounding facts</h3>
                            <ul className="mt-3 space-y-2 text-[#1F2624]">
                                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal shrink-0"></span><span>Around <strong>1 in 9</strong> Americans over 65 is living with dementia.</span></li>
                                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal shrink-0"></span><span>Dementia is <strong>not</strong> a normal part of aging — most people in their 80s do not have it.</span></li>
                                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal shrink-0"></span><span>Some forms can progress quickly, others over 10+ years. Each person’s journey is their own.</span></li>
                                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal shrink-0"></span><span>A handful of dementia-like conditions are <strong>treatable or even reversible</strong> — which is why proper testing matters.</span></li>
                            </ul>
                        </div>
                    </section>

                    {/* Risk factors */}
                    <section id="risk-factors" className="scroll-mt-32" data-testid="education-risk-factors">
                        <h2 className="text-3xl font-heading font-semibold tracking-tight text-[#12776F]">Risk factors</h2>
                        <p className="mt-4 text-[#1F2624] leading-relaxed">
                            A risk factor doesn’t mean someone will get dementia — it raises the odds. Some you can’t change.
                            Many you can. Research (notably the Lancet Commission’s review) estimates that
                            <strong> roughly 40% of dementia cases worldwide are linked to modifiable risks</strong>.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            <div className="ml-card p-5 bg-cream-dark !border-cream-dark">
                                <h3 className="font-heading font-medium text-[#1F2624]">Can’t change</h3>
                                <ul className="mt-3 space-y-2 text-sm text-[#1F2624]">
                                    <li className="flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#9CA6A1] shrink-0"></span><span><strong>Age</strong> — risk roughly doubles every five years after 65.</span></li>
                                    <li className="flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#9CA6A1] shrink-0"></span><span><strong>Family history</strong> — having a parent or sibling with Alzheimer’s raises risk modestly.</span></li>
                                    <li className="flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#9CA6A1] shrink-0"></span><span><strong>Genetics</strong> — APOE-e4 gene increases Alzheimer’s risk; rare familial gene mutations cause early-onset forms.</span></li>
                                    <li className="flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#9CA6A1] shrink-0"></span><span><strong>Sex</strong> — women are diagnosed more often, partly because they live longer.</span></li>
                                    <li className="flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#9CA6A1] shrink-0"></span><span><strong>Race / ethnicity</strong> — Black and Latino Americans are diagnosed at higher rates, largely tied to vascular health gaps.</span></li>
                                </ul>
                            </div>

                            <div className="ml-card p-5 bg-[#DDE5D3] !border-[#DDE5D3]">
                                <h3 className="font-heading font-medium text-[#4F6B3D]">Can change (or treat)</h3>
                                <ul className="mt-3 space-y-2 text-sm text-[#1F2624]">
                                    <li className="flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#4F6B3D] shrink-0"></span><span><strong>Heart health</strong> — high blood pressure, diabetes, high cholesterol, and obesity all raise risk.</span></li>
                                    <li className="flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#4F6B3D] shrink-0"></span><span><strong>Hearing loss</strong> — untreated mid-life hearing loss is one of the largest modifiable factors.</span></li>
                                    <li className="flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#4F6B3D] shrink-0"></span><span><strong>Smoking</strong> and <strong>heavy alcohol use</strong>.</span></li>
                                    <li className="flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#4F6B3D] shrink-0"></span><span><strong>Physical inactivity</strong> — even brisk walks help.</span></li>
                                    <li className="flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#4F6B3D] shrink-0"></span><span><strong>Head injury</strong> — wear seatbelts and helmets.</span></li>
                                    <li className="flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#4F6B3D] shrink-0"></span><span><strong>Depression</strong>, <strong>social isolation</strong>, and <strong>low cognitive engagement</strong>.</span></li>
                                    <li className="flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#4F6B3D] shrink-0"></span><span><strong>Air pollution</strong> and <strong>poor sleep</strong> (especially untreated sleep apnea).</span></li>
                                    <li className="flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#4F6B3D] shrink-0"></span><span><strong>Lower education in early life</strong> — building ”cognitive reserve” matters.</span></li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Diagnosis */}
                    <section id="diagnosis" className="scroll-mt-32" data-testid="education-diagnosis">
                        <h2 className="text-3xl font-heading font-semibold tracking-tight text-[#12776F]">How doctors test for it</h2>
                        <p className="mt-4 text-[#1F2624] leading-relaxed">
                            There is no single test for dementia. Doctors piece together a picture from
                            <strong> conversation, simple bedside tests, lab work, brain imaging,</strong> and sometimes
                            specialist evaluation. A thorough work-up usually unfolds over a few visits.
                        </p>

                        <ol className="mt-6 space-y-4">
                            {[
                                {
                                    n: "1",
                                    title: "History & physical exam",
                                    body: "The doctor asks about memory changes, daily function, mood, medications, alcohol, and family history. A family member or friend's account is invaluable here — bring someone who can describe what they've noticed.",
                                },
                                {
                                    n: "2",
                                    title: "Cognitive screening (10–30 minutes)",
                                    body: "Quick paper-and-pencil tests. Common ones: MMSE (Mini-Mental State Exam), MoCA (Montreal Cognitive Assessment), Mini-Cog, and SLUMS. They check memory, attention, language, and visuospatial skills. A low score doesn't equal dementia — it triggers more testing.",
                                },
                                {
                                    n: "3",
                                    title: "Blood work & basic labs",
                                    body: "To rule out reversible causes that mimic dementia: low vitamin B12, thyroid problems, infections, kidney/liver issues, depression, sleep disorders, and medication side effects. Always done.",
                                },
                                {
                                    n: "4",
                                    title: "Brain imaging",
                                    body: "A CT or MRI looks for strokes, tumors, bleeds, fluid buildup, and patterns of shrinkage. Most people get at least one scan during work-up.",
                                },
                                {
                                    n: "5",
                                    title: "Advanced tests (when needed)",
                                    body: "PET scans (amyloid or tau) — confirm Alzheimer's pathology. Lumbar puncture — measures amyloid/tau in spinal fluid. Newer blood biomarkers (e.g., p-tau217) are emerging. Detailed neuropsychological testing — 3–5 hour deep-dive for tricky cases or younger patients.",
                                },
                                {
                                    n: "6",
                                    title: "Specialist referral",
                                    body: "Geriatrician, neurologist, geriatric psychiatrist, or memory clinic. Especially if the picture is unusual — early onset, rapid changes, prominent behavior changes, or movement problems.",
                                },
                            ].map((step) => (
                                <li key={step.n} className="ml-card p-5 flex gap-4" data-testid={`education-step-${step.n}`}>
                                    <span className="shrink-0 w-9 h-9 rounded-full bg-teal text-white flex items-center justify-center font-heading font-semibold">
                                        {step.n}
                                    </span>
                                    <div>
                                        <h3 className="font-heading font-medium text-[#1F2624]">{step.title}</h3>
                                        <p className="mt-1 text-[#4C5551] leading-relaxed">{step.body}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>

                        <div className="ml-card p-5 mt-6 bg-yellow-soft !border-yellow-soft" data-testid="education-bring-list">
                            <h3 className="font-heading font-medium text-yellow-dark">Bring to the appointment</h3>
                            <p className="mt-2 text-sm text-[#1F2624]">
                                A list of <em>everything</em> your loved one takes (prescriptions, over-the-counter, supplements),
                                a timeline of changes you’ve noticed, and any prior cognitive test results. The Appointment Prep
                                tool in Memory Lane can put all of this on one page.
                            </p>
                        </div>
                    </section>

                    {/* Types */}
                    <section id="types" className="scroll-mt-32" data-testid="education-types">
                        <h2 className="text-3xl font-heading font-semibold tracking-tight text-[#12776F]">Types of dementia</h2>
                        <p className="mt-4 text-[#1F2624] leading-relaxed">
                            Knowing the type changes everything — medications that help one type can worsen another,
                            and behaviors that seem ”wrong” make sense once you know the underlying cause.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            {TYPES.map((t) => (
                                <article key={t.name} className={`ml-card p-5 border-2 ${TONE_STYLES[t.tone]}`}
                                    data-testid={`education-type-${t.name.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                                    <h3 className="font-heading font-medium text-lg">{t.name}</h3>
                                    <p className="text-xs uppercase tracking-wider font-semibold mt-1 opacity-80">{t.prevalence}</p>
                                    <p className="mt-3 text-sm text-[#1F2624] leading-relaxed">{t.hallmark}</p>
                                    <p className="mt-3 text-sm text-[#1F2624] leading-relaxed">
                                        <strong>Progression:</strong> {t.progression}
                                    </p>
                                </article>
                            ))}
                        </div>
                    </section>

                    {/* FAST scale */}
                    <section id="fast-scale" className="scroll-mt-32" data-testid="education-fast">
                        <h2 className="text-3xl font-heading font-semibold tracking-tight text-[#12776F]">FAST stages — a roadmap for what’s coming</h2>
                        <p className="mt-4 text-[#1F2624] leading-relaxed">
                            The <strong>Functional Assessment Staging Tool (FAST)</strong>, developed by Dr. Barry Reisberg,
                            describes 7 stages of Alzheimer’s progression based on <em>what someone can still do</em> — not
                            just what they remember. It’s the most caregiver-friendly scale because it focuses on function,
                            not test scores.
                        </p>
                        <p className="mt-3 text-sm text-[#4C5551] leading-relaxed">
                            Knowing the stage helps you anticipate needs, qualify for services (Medicare hospice eligibility
                            begins at <strong>FAST 7c</strong>), and prepare emotionally.
                        </p>

                        <ol className="mt-6 space-y-3">
                            {FAST_STAGES.map((s) => (
                                <li key={s.n}
                                    className={`ml-card p-5 border-2 ${TONE_STYLES[s.tone]}`}
                                    data-testid={`education-fast-${s.n}`}>
                                    <div className="flex gap-4 items-start">
                                        <span className="shrink-0 w-12 h-12 rounded-full bg-white/70 flex items-center justify-center font-heading font-semibold text-xl">
                                            {s.n}
                                        </span>
                                        <div>
                                            <h3 className="font-heading font-medium text-lg text-[#1F2624]">Stage {s.n} — {s.name}</h3>
                                            <p className="mt-1 text-[#1F2624] leading-relaxed">{s.caregiver}</p>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ol>

                        {/* Self-assessment */}
                        <FastAssessment />

                        <div className="ml-card p-5 mt-6 bg-coral-light !border-coral-light" data-testid="education-fast-note">
                            <h3 className="font-heading font-medium text-coral-dark">A note about stages</h3>
                            <p className="mt-2 text-sm text-[#1F2624]">
                                These stages are an average path, not a schedule. People often plateau for months or years.
                                They can also skip or blur stages, especially in non-Alzheimer’s dementias. Use stages to plan
                                — never to predict.
                            </p>
                        </div>
                    </section>

                    {/* Knowledge Library — the full searchable catalog of
                        Memory Lane caregiver guides (static SEO + Studio
                        published), grouped by category with a search bar.
                        Shared component with the standalone /library route
                        so caregivers find the same content from either entry
                        point. */}
                    <section
                        id="knowledge-library"
                        className="scroll-mt-32"
                        data-testid="education-knowledge-library"
                    >
                        <header className="flex items-start gap-3 mb-4">
                            <span className="w-12 h-12 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center shrink-0">
                                <BookOpen size={20} strokeWidth={1.8} />
                            </span>
                            <div>
                                <h2 className="text-2xl font-heading font-medium text-teal-dark">
                                    Knowledge library
                                </h2>
                                <p className="text-sm text-[#4C5551] mt-1 max-w-2xl">
                                    Every Memory Lane guide in one searchable, categorized library — from early-stage planning to advanced care.
                                </p>
                            </div>
                        </header>
                        <KnowledgeLibrarySection
                            showHeader={false}
                            testidPrefix="learn-library"
                            defaultCollapsed={true}
                        />
                    </section>

                    {/* Footer resources */}
                    <section className="ml-card p-6 md:p-8 bg-teal-soft !border-teal-soft" data-testid="education-resources">
                        <h2 className="text-2xl font-heading font-medium text-teal-dark">Want to go deeper?</h2>
                        <p className="mt-2 text-[#1F2624]">
                            These are the sources caregivers and clinicians trust most:
                        </p>
                        <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <li>
                                <a href="https://www.alz.org" target="_blank" rel="noreferrer"
                                    data-testid="education-link-alz"
                                    className="inline-flex items-center gap-2 text-teal-dark hover:text-teal font-medium underline-offset-2 hover:underline">
                                    <ExternalLink size={16} strokeWidth={1.6} /> Alzheimer’s Association (alz.org)
                                </a>
                            </li>
                            <li>
                                <a href="https://www.nia.nih.gov/health/alzheimers-and-dementia" target="_blank" rel="noreferrer"
                                    data-testid="education-link-nia"
                                    className="inline-flex items-center gap-2 text-teal-dark hover:text-teal font-medium underline-offset-2 hover:underline">
                                    <ExternalLink size={16} strokeWidth={1.6} /> NIH National Institute on Aging
                                </a>
                            </li>
                            <li>
                                <a href="https://www.lbda.org" target="_blank" rel="noreferrer"
                                    data-testid="education-link-lbda"
                                    className="inline-flex items-center gap-2 text-teal-dark hover:text-teal font-medium underline-offset-2 hover:underline">
                                    <ExternalLink size={16} strokeWidth={1.6} /> Lewy Body Dementia Association
                                </a>
                            </li>
                            <li>
                                <a href="https://www.theaftd.org" target="_blank" rel="noreferrer"
                                    data-testid="education-link-aftd"
                                    className="inline-flex items-center gap-2 text-teal-dark hover:text-teal font-medium underline-offset-2 hover:underline">
                                    <ExternalLink size={16} strokeWidth={1.6} /> Association for Frontotemporal Degeneration
                                </a>
                            </li>
                        </ul>

                        <div className="mt-6 pt-6 border-t border-teal/20 flex items-center gap-3">
                            <Phone size={18} strokeWidth={1.6} className="text-teal-dark shrink-0" />
                            <p className="text-sm text-[#1F2624]">
                                Need to talk to a real person? <strong>Alzheimer’s Association 24/7 Helpline:</strong>{" "}
                                <a href="tel:18002723900" className="text-teal-dark font-medium underline underline-offset-2">
                                    1-800-272-3900
                                </a>
                                . Free. Multilingual. Always answers.
                            </p>
                        </div>
                    </section>
                </article>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  FAST self-assessment — gentle yes/no quiz; highest "yes" stage wins        */
/* -------------------------------------------------------------------------- */

function FastAssessment() {
    const [answers, setAnswers] = useState({}); // { 0: "yes" | "no", 1: ... }
    const [submitted, setSubmitted] = useState(false);

    const allAnswered = FAST_QUIZ.every((_, i) => answers[i]);

    const estimatedStage = useMemo(() => {
        // Highest-numbered question marked "yes" → its stage. None → stage 1.
        let highest = 1;
        FAST_QUIZ.forEach((q, i) => {
            if (answers[i] === "yes" && q.stage > highest) highest = q.stage;
        });
        return highest;
    }, [answers]);

    const result = QUIZ_RESULT_COPY[estimatedStage];

    const setAns = (i, v) => setAnswers((a) => ({ ...a, [i]: v }));
    const reset = () => { setAnswers({}); setSubmitted(false); };

    return (
        <div className="ml-card p-6 md:p-8 mt-8 bg-cream-dark !border-cream-dark" data-testid="fast-quiz">
            <div className="flex items-center gap-3">
                <ClipboardCheck strokeWidth={1.6} className="text-teal" />
                <h3 className="text-2xl font-heading font-medium text-[#12776F]">
                    Take a 1-minute FAST self-check
                </h3>
            </div>
            <p className="mt-2 text-[#4C5551] leading-relaxed">
                Seven yes-or-no questions about your loved one. We’ll give you a gentle estimate of where they
                might be on the FAST scale. <strong>This is not a diagnosis</strong> — it’s a starting point for
                conversations with their doctor.
            </p>

            {!submitted && (
                <>
                    <ol className="mt-6 space-y-4" data-testid="fast-quiz-questions">
                        {FAST_QUIZ.map((q, i) => (
                            <li key={i} className="ml-card p-5 bg-white" data-testid={`fast-quiz-q-${i}`}>
                                <p className="text-[#1F2624] leading-relaxed">
                                    <span className="font-heading font-semibold text-teal-dark mr-2">{i + 1}.</span>
                                    {q.q}
                                </p>
                                <div className="flex gap-2 mt-3">
                                    {["yes", "no"].map((v) => (
                                        <button
                                            key={v}
                                            type="button"
                                            data-testid={`fast-quiz-${i}-${v}`}
                                            onClick={() => setAns(i, v)}
                                            className={`flex-1 sm:flex-none sm:min-w-[120px] py-2.5 px-5 rounded-2xl text-sm font-medium border transition-all ${answers[i] === v
                                                ? (v === "yes"
                                                    ? "bg-teal text-white border-teal"
                                                    : "bg-[#4C5551] text-white border-[#4C5551]")
                                                : "bg-white border-[#E2E8F0] text-[#4C5551] hover:bg-cream-dark"
                                                }`}
                                        >
                                            {v === "yes" ? "Yes" : "No / not yet"}
                                        </button>
                                    ))}
                                </div>
                            </li>
                        ))}
                    </ol>

                    <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
                        <p className="text-xs text-[#4C5551]">
                            {Object.keys(answers).length} of {FAST_QUIZ.length} answered
                        </p>
                        <button
                            type="button"
                            onClick={() => setSubmitted(true)}
                            disabled={!allAnswered}
                            data-testid="fast-quiz-submit"
                            className="ml-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            See my estimate <ArrowRight size={18} strokeWidth={1.8} />
                        </button>
                    </div>
                </>
            )}

            {submitted && (
                <div className="mt-6 space-y-4" data-testid="fast-quiz-result">
                    <div className={`ml-card p-6 md:p-8 border-2 ${TONE_STYLES[result.tone]}`}>
                        <p className="text-xs uppercase tracking-wider font-semibold opacity-80">Your estimate</p>
                        <h4 className="mt-1 text-2xl md:text-3xl font-heading font-semibold text-[#12776F]">
                            {result.title}
                        </h4>
                        <p className="mt-3 text-[#1F2624] leading-relaxed">{result.body}</p>
                    </div>

                    <div className="ml-card p-5 bg-white" data-testid="fast-quiz-next">
                        <h4 className="font-heading font-medium text-[#1F2624]">Where to go from here</h4>
                        <ul className="mt-3 space-y-2.5 text-sm">
                            <li className="flex gap-3">
                                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal shrink-0"></span>
                                <span className="text-[#1F2624]">
                                    See the full <Link to="/roadmap" data-testid="fast-quiz-link-roadmap"
                                        className="text-teal-dark font-medium underline underline-offset-2">caregiver roadmap</Link> —
                                    what to expect, what to plan for, and what helps at this stage.
                                </span>
                            </li>
                            <li className="flex gap-3">
                                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal shrink-0"></span>
                                <span className="text-[#1F2624]">
                                    Bring this estimate to a doctor visit — the <Link to="/appointment" data-testid="fast-quiz-link-appt"
                                        className="text-teal-dark font-medium underline underline-offset-2">Appointment Prep</Link> tool
                                    builds a printable summary.
                                </span>
                            </li>
                            {estimatedStage >= 4 && (
                                <li className="flex gap-3">
                                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal shrink-0"></span>
                                    <span className="text-[#1F2624]">
                                        Find <Link to="/help-finder" data-testid="fast-quiz-link-resources"
                                            className="text-teal-dark font-medium underline underline-offset-2">local support resources</Link> —
                                        adult day programs, respite care, and in-home help in your state.
                                    </span>
                                </li>
                            )}
                            {estimatedStage >= 6 && (
                                <li className="flex gap-3">
                                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal shrink-0"></span>
                                    <span className="text-[#1F2624]">
                                        Take care of yourself too — try the{" "}
                                        <Link to="/burnout" data-testid="fast-quiz-link-burnout"
                                            className="text-teal-dark font-medium underline underline-offset-2">burnout check-in</Link>.
                                        Stage 6+ caregiving is among the most demanding work in the world.
                                    </span>
                                </li>
                            )}
                        </ul>
                    </div>

                    <button
                        type="button"
                        onClick={reset}
                        data-testid="fast-quiz-reset"
                        className="ml-btn-secondary"
                    >
                        <RefreshCcw size={18} strokeWidth={1.6} /> Retake the check
                    </button>
                </div>
            )}
        </div>
    );
}





